import { isNumeric } from '../../util/helpers';

import { TakeoffLandArea } from './takeoff-land-area.model';


/**
 * Takeoff/land area type
 */
export enum LandingSurfaceType {
  Runway,
  Waterway,
  Helipad
}


/**
 * Landing surface condition
 */
export enum LandingSurfaceCondition {
  Excellent,
  Good,
  Fair,
  Poor,
  Failed,
  NotReported
}


/**
 * Pavement type (flexible or rigid)
 */
export enum PavementType {
  Flexible,
  Rigid
}


/**
 * Subgrade Strength classification
 */
export enum SubgradeStrength {
  UltraLow,
  Low,
  Medium,
  High
}


/**
 * Tire pressure classification
 */
export enum TirePressure {
  UltraLow,
  Low,
  Medium,
  High
}


/**
 * Edge lighting type
 */
export enum EdgeLighting {
  High,
  Low,
  Strobe,
  Medium,
  Flood,
  Perimeter,
  NonStandard
}


/**
 * Landing surface model (e.g., runway, waterway, or helipad). For runways, there is a single LandingSurface model which
 * encompasses both ends (e.g., runway "05/23"). Each reciprocal end of the runway is modeled as a TakeoffLandArea (so
 * a LandingSurface has one or more takeoff/land areas). Helipads only have a single TakeoffLandArea.
 */
export class LandingSurface {
  /**
   * Database id for the landing surface
   */
  landingSurfaceId: number;

  /**
   * Database airport Id that the runway belongs to
   */
  airportId: number;

  /**
   * Display name of landing surface. For runways, this typically includes both the base and reciprocal end (e.g.,
   * "12/20").
   */
  name: string;

  /**
   * Landing surface type (runway, helipad, waterway)
   */
  type: LandingSurfaceType;

  /**
   * Length of landing surface in feet
   */
  length: number;

  /**
   * Length of landing surface in meters
   */
  lengthMeters: number;

  /**
   * Width of landing surface in feet
   */
  width: number;

  /**
   * Width of landing surface in meters
   */
  widthMeters: number;

  /**
   * Material 1 type
   */
  materialType1: string;

  /**
   * Material 2 type
   */
  materialType2: string;

  /**
   * Surface treatment
   */
  treatment: string;

  /**
   * Landing surface condition
   */
  condition: LandingSurfaceCondition;

  /**
   * Runway surface description as a readable string. This incorporates the material type 1 and 2, treatment, and
   * condition fields.
   */
  surfaceReadable: string;

  /**
   * Pavement classification number (PCN). This is a rating system that expresses the relative load carrying capacity of
   * a pavement in terms of a standard wheel load. The rating is structured so that a pavement with a particular PCN
   * value can support, without weight restrictions, an aircraft which has an aircraft classification number (ACN) equal
   * to or less than the pavement's PCN value.
   */
  pcn: number;

  /**
   * Runway weight-bearing capacity for single-wheel type landing gear (DC3, C47, F15, etc.)
   */
  wtBearing1Wheel: number;

  /**
   * Runway weight-bearing capacity for two wheel type landing gear
   */
  wtBearing2Wheel: number;

  /**
   * Runway weight-bearing capacity for two wheel tandem type landing gear
   */
  wtBearing2WheelTandem: number;

  /**
   * Runway weight-bearing capacity for two wheel double tandem type landing gear
   */
  wtBearing2WheelDblTandem: number;

  /**
   * Pavement type
   */
  pavementType: PavementType;

  /**
   * Subgrade strength classification
   */
  subgradeStrength: SubgradeStrength;

  /**
   * Tire pressure classification
   */
  tirePressure: TirePressure;

  /**
   * Edge lighting type
   */
  edgeLighting: EdgeLighting;

  /**
   * List of takeoff and landing areas associated with this landing surface (e.g., for a runway, this would be the two
   * reciprocal ends of the runway).
   */
  takeOffLandAreas: TakeoffLandArea[];

  /**
   * Returns true if the runway surface is paved
   */
  get paved(): boolean {
    return this.materialType1 === 'asphalt' || this.materialType1 === 'concrete' || this.materialType1 === 'pem';
  }

  /**
   * Returns the runway named transformed so it can be used as part of a URL fragment (to allow linking directly
   * to the appropriate runway detail section of the page).
   * @returns {string}
   */
  get uriFragment(): string {
    return 'rwy' + this.name.toUpperCase()
      .replace('/', '-')
      .replace(/[^0-9A-Z-]/g, '');
  }

  /**
   * Parses landing surface
   * @param data {any} Landing surface data as returned by API
   * @return {LandingSurface} Parsed landing surface
   */
  static parse(data: any): LandingSurface {
    return Object.assign(new LandingSurface(), {
      landingSurfaceId: data.landingSurfaceId,
      airportId: data.airportId,
      name: data.name || '',
      type: this.parseLandingSurfaceType(data.areaType),
      length: isNumeric(data.length) ? +data.length : null,
      lengthMeters: isNumeric(data.length) ? +Math.round(data.length / 3.28084) : null,
      width: isNumeric(data.width) ? +data.width : null,
      widthMeters: isNumeric(data.width) ? +Math.round(data.width / 3.28084) : null,
      materialType1: data.materialType1 || '',
      materialType2: data.materialType2 || '',
      treatment: data.treatment || '',
      condition: this.parseCondition(data.condition),
      surfaceReadable: data.surfaceReadable,
      pcn: isNumeric(data.pcn) ? +data.pcn : null,
      wtBearing1Wheel: isNumeric(data.wtBearing1Wheel) ? +data.wtBearing1Wheel : null,
      wtBearing2Wheel: isNumeric(data.wtBearing2Wheel) ? +data.wtBearing2Wheel : null,
      wtBearing2WheelTandem: isNumeric(data.wtBearing2WheelTandem) ? +data.wtBearing2WheelTandem : null,
      wtBearing2WheelDblTandem: isNumeric(data.wtBearing2WheelDblTandem) ? +data.wtBearing2WheelDblTandem : null,
      pavementType: this.parsePavementType(data.pavementType),
      subgradeStrength: this.parseSubgradeStrength(data.subgradeStrength),
      tirePressure: this.parseTirePressure(data.tirePressure),
      edgeLighting: this.parseEdgeLighting(data.edgeLighting),
      runwayedgeLights: data.edgeLighting,
      takeOffLandAreas: (data.takeOffLandAreas || []).map(a => TakeoffLandArea.parse(a))
    });
  }

  /**
   * Parses landing surface type from string
   * @param value {string} Landing surface type as returned by API
   * @return {LandingSurfaceType} Parsed landing surface type
   */
  static parseLandingSurfaceType(value: string): LandingSurfaceType {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'runway':
        return LandingSurfaceType.Runway;
      case 'waterway':
        return LandingSurfaceType.Waterway;
      case 'pad':
        return LandingSurfaceType.Helipad;
      default:
        return null;
    }
  }

  /**
   * Formats landing surface type for display
   * @param {LandingSurfaceType} value
   * @returns {string}
   */
  static formatLandingSurfaceType(value: LandingSurfaceType): string {
    switch (value) {
      case LandingSurfaceType.Runway:
        return 'Runway';
      case LandingSurfaceType.Waterway:
        return 'Waterway';
      case LandingSurfaceType.Helipad:
        return 'Helipad';
      default:
        return '';
    }
  }

  /**
   * Parses landing surface condition
   * @param value {any} Landing surface condition as returned by API
   * @return {LandingSurfaceCondition} Parsed landing surface condition
   */
  static parseCondition(value: any): LandingSurfaceCondition {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'good':
        return LandingSurfaceCondition.Good;
      case 'failed':
        return LandingSurfaceCondition.Failed;
      case 'poor':
        return LandingSurfaceCondition.Poor;
      case 'excellent':
        return LandingSurfaceCondition.Excellent;
      case 'fair':
        return LandingSurfaceCondition.Fair;
      case 'not reported':
        return LandingSurfaceCondition.NotReported;
      default:
        return null;
    }
  }

  /**
   * Formats landing surface condition
   * @param value {LandingSurfaceCondition} Landing sufrace condition enum
   * @return string Formatted landing surface string
   */
  static formatCondition(value: LandingSurfaceCondition): string {
    switch (value) {
      case LandingSurfaceCondition.Good:
        return 'Good';
      case LandingSurfaceCondition.Excellent:
        return 'Excellent';
      case LandingSurfaceCondition.Failed:
        return 'Failed';
      case LandingSurfaceCondition.Fair:
        return 'Fair';
      case LandingSurfaceCondition.Poor:
        return 'Poor';
      case LandingSurfaceCondition.NotReported:
        return 'Not Reported';
      default:
        return null;
    }
  }

  /**
   * Parses pavement type
   * @param value {any} Pavement type as returned by API
   * @return {PavementType} Parsed pavement type
   */
  static parsePavementType(value: any): PavementType {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'flexible':
        return PavementType.Flexible;
      case 'rigid':
        return PavementType.Rigid;
      default:
        return null;
    }
  }

  /**
   * Formats pavement type
   * @param value {PavementType} the pavement type enum
   * @return {string} the formatted pavement type string
   */
  static formatPavementType(value: PavementType): string {
    switch (value) {
      case PavementType.Flexible:
        return 'Flexible';
      case PavementType.Rigid:
        return 'Rigid';
      default:
        return null;
    }
  }

  /**
   * Parses subgrade strength
   * @param value {any} Subgrade strength as returned by API
   * @return {SubgradeStrength} Parsed subgrade strength
   */
  static parseSubgradeStrength(value: any): SubgradeStrength {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'ultra low':
        return SubgradeStrength.UltraLow;
      case 'low':
        return SubgradeStrength.Low;
      case 'medium':
        return SubgradeStrength.Medium;
      case 'high':
        return SubgradeStrength.High;
      default:
        return null;
    }
  }

  /**
   * Parses tire pressure
   * @param value {any} Tire pressure as returned by API
   * @return {TirePressure} Parsed tire pressure
   */
  static parseTirePressure(value: any): TirePressure {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'ultra low':
        return TirePressure.UltraLow;
      case 'low':
        return TirePressure.Low;
      case 'medium':
        return TirePressure.Medium;
      case 'high':
        return TirePressure.High;
      default:
        return null;
    }
  }

  /**
   * Parses edge lighting type
   * @param value {any} Edge lighting type as returned by API
   * @return {TirePressure} Parsed edge lighting
   */
  static parseEdgeLighting(value: any): EdgeLighting {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'high':
        return EdgeLighting.High;
      case 'low':
        return EdgeLighting.Low;
      case 'strobe':
        return EdgeLighting.Strobe;
      case 'medium':
        return EdgeLighting.Medium;
      case 'flood':
        return EdgeLighting.Flood;
      case 'perimeter':
        return EdgeLighting.Perimeter;
      case 'non-std':
        return EdgeLighting.NonStandard;
      default:
        return null;
    }
  }

  /**
   * Formats edge lighting for display
   */
  static formatEdgeLighting(value: EdgeLighting): string {
    switch (value) {
      case EdgeLighting.High:
        return 'High';
      case EdgeLighting.Low:
        return 'Low';
      case EdgeLighting.Strobe:
        return 'Strobe';
      case EdgeLighting.Medium:
        return 'Medium';
      case EdgeLighting.Flood:
        return 'Flood';
      case EdgeLighting.Perimeter:
        return 'Perimeter';
      case EdgeLighting.NonStandard:
        return 'Non-standard';
      default:
        return null;
    }
  }
}
